import { reportingApi, fetchImageAsDataUrl } from '../../services/api';
import { fmtDate, fmtDataRows } from '../reports/pdf/prepareReportPdf';
import exportReportPdf from '../reports/export/reportPdfExporter';
import wmLogo from '../../assets/logos/WM-Logo-Full.png';

/**
 * Exports a Submittal Package's index sheets as the standard "Index Report - Tags" PDF, one
 * report per index sheet — either one file per index sheet or one combined file.
 *
 * REUSE, READ-ONLY. Every standard report, INCLUDING the locked I/O Assignments, renders through
 * ReportViewerPage and the shared exporter. This module only imports and calls:
 *   - reportingApi.getConfig / runReport — the report's own config and query, filtered through
 *     its existing Index_Sheet parameter (an FK, so an `equals` entry matches the id, and the
 *     entries of one parameter are OR'd server-side);
 *   - exportReportPdf — the shared worker-backed PDF exporter, unchanged.
 * The column-width and spec-header preparation ReportViewerPage does before calling the
 * exporter lives inside that page and is not exported, so the index-report part of it is
 * COPIED here rather than extracted — extracting would edit the file every report depends on.
 * pdf/DsPackagePdfDocument.js makes the same call for the same reason.
 *
 * The rows are then narrowed to the package's own tags, so each PDF shows the index sheet as the
 * package carries it rather than every tag in the project that shares the sheet.
 */

export const INDEX_REPORT_SLUG = 'slugRptStd-Sub-Index_Report_Tags';
const INDEX_REPORT_LABEL = 'Index Report - Tags';
const GROUP_FIELD = 'Index_Sheet';

// SQL Server caps a command at 2100 parameters and every id is one, so a large package is
// fetched in chunks well under that.
const IDS_PER_RUN = 500;

// Copied from ReportViewerPage — keep in step with it.
const HIDDEN_FIELDS = new Set([
  'Index_Sheet', 'IDX_SPEC_1_COL', 'IDX_SPEC_2_COL',
  'IDX_SPEC_3_COL', 'IDX_SPEC_4_COL', 'IDX_SPEC_5_COL',
]);
const SPEC_COL_MAP = [
  ['IDX_SPEC_1_COL', 'IDX_SPEC_1'],
  ['IDX_SPEC_2_COL', 'IDX_SPEC_2'],
  ['IDX_SPEC_3_COL', 'IDX_SPEC_3'],
  ['IDX_SPEC_4_COL', 'IDX_SPEC_4'],
  ['IDX_SPEC_5_COL', 'IDX_SPEC_5'],
];
const WRAPPABLE_FIELDS = new Set(['Note', 'Comment', 'Note_DES']);
const PDF_PAGE_WIDTH_PT = 1176; // tabloid landscape 1224pt - 48pt padding
const ROWS_PER_PAGE = 40;
const CELL_PAD_PT = 4;

const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

let measureCtx = null;
const measureTextPx = (text, font) => {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = font;
  return measureCtx.measureText(String(text)).width;
};

const splitHeaderLabel = (label) => {
  const words = String(label || '').split(' ');
  if (words.length <= 1) return [label, ''];
  if (words.length === 2) return [words[0], words[1]];
  let bestSplit = 1, bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const m = Math.max(words.slice(0, i).join(' ').length, words.slice(i).join(' ').length);
    if (m < bestMax) { bestMax = m; bestSplit = i; }
  }
  return [words.slice(0, bestSplit).join(' '), words.slice(bestSplit).join(' ')];
};

const applySpecLabels = (cols, firstRow) =>
  cols.map(c => {
    const entry = SPEC_COL_MAP.find(([, sf]) => sf === c.field);
    if (!entry || !firstRow) return c;
    const cf = toCamelCase(entry[0]);
    return { ...c, label: firstRow[`${cf}_Display`] || firstRow[cf] || c.label };
  });

const groupKey = (row) => {
  const cf = toCamelCase(GROUP_FIELD);
  const v = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
  return v != null ? String(v) : '(Blank)';
};

// Content-aware widths, as ReportViewerPage computes them for the PDF: every value fits on one
// line, and the spare page width is shared out by the config's column proportions.
const computeColumnWidths = (cols, rows, fontSize) => {
  const dataFont = `${fontSize}px Helvetica, sans-serif`;
  const headerFont = `bold ${fontSize}px Helvetica, sans-serif`;
  const ptFactor = fontSize <= 6 ? 0.75 : 1.0;
  const minWidths = {};
  cols.forEach(c => {
    const cf = toCamelCase(c.field);
    let maxDataPx = 0;
    if (!WRAPPABLE_FIELDS.has(c.field)) {
      rows.forEach(row => {
        const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
        if (val != null) maxDataPx = Math.max(maxDataPx, measureTextPx(String(fmtDate(val)), dataFont));
      });
    }
    let headerPx;
    const specEntry = SPEC_COL_MAP.find(([, sf]) => sf === c.field);
    if (specEntry) {
      const labelCf = toCamelCase(specEntry[0]);
      let maxLabelPx = 0;
      rows.forEach(row => {
        const lbl = row[`${labelCf}_Display`] || row[labelCf];
        if (lbl) maxLabelPx = Math.max(maxLabelPx, measureTextPx(String(lbl), headerFont));
      });
      headerPx = maxLabelPx || measureTextPx(c.label, headerFont);
    } else {
      const [h1, h2] = splitHeaderLabel(c.label);
      headerPx = Math.max(measureTextPx(h1, headerFont), h2 ? measureTextPx(h2, headerFont) : 0);
    }
    minWidths[c.field] = Math.max(40, Math.ceil(Math.max(maxDataPx, headerPx) * ptFactor) + CELL_PAD_PT);
  });
  if (minWidths.Note != null) minWidths.Note = Math.max(minWidths.Note, minWidths.Description || minWidths.Tag_Description || 50);
  if (minWidths.Comment != null) minWidths.Comment = Math.max(minWidths.Comment, 175);
  if (minWidths.Note_DES != null) minWidths.Note_DES = Math.max(minWidths.Note_DES, 175);

  const totalMin = cols.reduce((sum, c) => sum + minWidths[c.field], 0);
  const widths = {};
  if (totalMin >= PDF_PAGE_WIDTH_PT) {
    cols.forEach(c => { widths[c.field] = `${((minWidths[c.field] / totalMin) * 100).toFixed(1)}%`; });
  } else {
    const remaining = PDF_PAGE_WIDTH_PT - totalMin;
    const totalConfigPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
    cols.forEach(c => {
      const share = (c.pixelWidth || c.width * 10 || 100) / totalConfigPx;
      widths[c.field] = `${(((minWidths[c.field] + remaining * share) / PDF_PAGE_WIDTH_PT) * 100).toFixed(1)}%`;
    });
  }
  return widths;
};

// The logo is decoration: when it cannot be fetched the report still exports without it.
const loadClientLogo = async (iconUrl) => (await fetchImageAsDataUrl(iconUrl)) || undefined;

/**
 * Runs the index report for the given index sheets, narrowed to the package's tags, and prepares
 * everything ReportPdfDocument needs. Shared by the standalone export below and by
 * buildIndexSheetReportProps, so both print the same pages for the same index sheet.
 *
 * @returns {Promise<null | object>} null when no package tag sits on any of the index sheets
 */
async function prepareIndexSheetReport({ indexSheetIds, tagRowIds, sourceLabel, currentDb, onProgress }) {
  onProgress?.('Loading the index report…');
  const [{ data: config }, clientLogoSrc] = await Promise.all([
    reportingApi.getConfig(INDEX_REPORT_SLUG),
    loadClientLogo(currentDb?.iconUrl),
  ]);

  const rows = [];
  for (let i = 0; i < indexSheetIds.length; i += IDS_PER_RUN) {
    onProgress?.(`Running the index report (${Math.min(i + IDS_PER_RUN, indexSheetIds.length)} of ${indexSheetIds.length} index sheets)…`);
    const chunk = indexSheetIds.slice(i, i + IDS_PER_RUN);
    const res = await reportingApi.runReport(INDEX_REPORT_SLUG, {
      parameters: {
        [toCamelCase(GROUP_FIELD)]: chunk.map(id => ({ operator: 'equals', value: String(id) })),
      },
    });
    rows.push(...(res.data?.data ?? []));
  }

  const tagSet = new Set(tagRowIds);
  const packageRows = rows.filter(r => tagSet.has(r.id));
  if (packageRows.length === 0) return null;

  const reportName = config.reportName || INDEX_REPORT_LABEL;
  const fontSize = config.layout?.fontSize || 8;
  const cols = [...(config.columns || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter(c => !HIDDEN_FIELDS.has(c.field));
  const colWidths = computeColumnWidths(cols, packageRows, fontSize);

  // Group rows by index sheet, in index-sheet order, with that sheet's spec labels as headers.
  const groups = new Map();
  packageRows.forEach(r => {
    const k = groupKey(r);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  });
  const ordered = [...groups.entries()].sort(([a], [b]) => {
    if (a === '(Blank)') return 1;
    if (b === '(Blank)') return -1;
    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
  });
  const columnsByGroup = Object.fromEntries(ordered.map(([k, g]) => [k, applySpecLabels(cols, g[0])]));

  // Same header treatment ReportViewerPage gives an index report: the Index_Sheet parameter is
  // the per-page subtitle, and label/watermark parameters are display-only.
  const headerConfig = {
    ...config,
    parameters: (config.parameters || [])
      .filter(p => p.field !== GROUP_FIELD && p.type !== 'label' && p.type !== 'watermark'),
  };
  const parameters = sourceLabel ? { Source: [{ display: sourceLabel }] } : {};
  const clientName = currentDb?.description || currentDb?.displayName || undefined;
  const common = {
    columnWidths: colWidths,
    parameters,
    paramHeaderWidths: {},
    logoSrc: wmLogo,
    clientLogoSrc,
    clientName,
    groupByField: GROUP_FIELD,
    rowsPerPage: ROWS_PER_PAGE,
  };

  return { reportName, headerConfig, cols, ordered, columnsByGroup, common };
}

/**
 * The index report as ReportPdfDocument props, one prop set per index sheet, keyed by the
 * index sheet's ID (as a string).
 *
 * Keyed by ID rather than by the Index_Sheet label: the report row's raw `index_Sheet` is the
 * Tags.Index_Sheet FK and a package record carries the same FK as `indexSheetId`, so the match is
 * exact. The label is only the FK's display column and is not guaranteed unique.
 *
 * Each prop set is the grouped-mode props the combined export passes, holding only that index
 * sheet's rows, so it renders exactly that sheet's pages of the combined report. Everything in it
 * is plain data (the logos are URL / data-URL strings), so it can be posted to a worker.
 *
 * @param {object}   opts               as exportIndexSheetReports, without `separate`
 * @returns {Promise<Object<string, object>>} empty when no package tag sits on the index sheets
 */
export async function buildIndexSheetReportProps({
  indexSheetIds, tagRowIds, sourceLabel, currentDb, onProgress,
}) {
  const prep = await prepareIndexSheetReport({ indexSheetIds, tagRowIds, sourceLabel, currentDb, onProgress });
  if (!prep) return {};
  const { reportName, headerConfig, cols, ordered, columnsByGroup, common } = prep;

  const idField = toCamelCase(GROUP_FIELD);
  const byId = {};
  ordered.forEach(([k, g]) => {
    g.forEach(r => {
      const id = r[idField];
      if (id == null) return;
      const key = String(id);
      if (!byId[key]) {
        byId[key] = {
          ...common,
          // The report's own watermark is dropped here: in a package the watermark belongs to
          // the datasheet pages alone.
          config: { ...headerConfig, reportName, watermark: undefined },
          columns: cols,
          columnsByGroup: { [k]: columnsByGroup[k] },
          data: [],
        };
      }
      byId[key].data.push(r);
    });
  });
  Object.values(byId).forEach(p => { p.data = fmtDataRows(p.data); });
  return byId;
}

/**
 * @param {object}   opts
 * @param {number[]} opts.indexSheetIds  the package's distinct index sheet ids
 * @param {number[]} opts.tagRowIds      the package's Tags.ID values, to narrow the rows
 * @param {string}   opts.sourceLabel    printed in the report's Source header slot
 * @param {boolean}  opts.separate       true = one file per index sheet, false = one combined file
 * @param {object}   [opts.currentDb]    for the client logo and confidentiality name
 * @param {function} [opts.onProgress]   (message) => void
 * @returns {Promise<number>} the number of PDFs written
 */
export default async function exportIndexSheetReports({
  indexSheetIds, tagRowIds, sourceLabel, separate, currentDb, onProgress,
}) {
  const prep = await prepareIndexSheetReport({ indexSheetIds, tagRowIds, sourceLabel, currentDb, onProgress });
  if (!prep) return 0;
  const { reportName, headerConfig, cols, ordered, columnsByGroup, common } = prep;

  if (!separate) {
    onProgress?.(`Rendering ${ordered.length} index sheets into one PDF…`);
    const ok = await exportReportPdf({
      ...common,
      config: { ...headerConfig, reportName },
      columns: cols,
      data: fmtDataRows(ordered.flatMap(([, g]) => g)),
      columnsByGroup,
    });
    return ok ? 1 : 0;
  }

  let written = 0;
  for (const [k, g] of ordered) {
    onProgress?.(`Rendering ${k} (${written + 1} of ${ordered.length})…`);
    const ok = await exportReportPdf({
      ...common,
      config: { ...headerConfig, reportName: `${reportName} - ${k}` },
      columns: columnsByGroup[k],
      data: fmtDataRows(g),
      autoDownload: true,
    });
    if (!ok) break;
    written += 1;
  }
  return written;
}
